import { TFabricVariant } from '../api/ba-app.api';
import { TPaginationInfo, TRESPONSE_LotListing, TRollInfo } from '../input-app/fabric-inspection/api.types';
import { EGRNGenerationStatus, EItemType } from './common.types';
import { TMaterialRequestsAppDetails } from './material-requests.types';

export type TGRNListingFilterRequest = {
  filterType: string;
  values: string[];
};

export enum EGRNModuleTypeForPayload {
  CREATE_GRN = 'create_grn',
  STORAGE = 'storage',
}

export enum EListingType {
  GRN_APP = 'grn_app',
}

export type TREQUEST_FabricGRNLotListing = {
  appDetails: TMaterialRequestsAppDetails;
  filters: TGRNListingFilterRequest[];
  pageNo: number;
  pageSize: number;
  itemType: EItemType.FABRIC_INSPECTION;
  listingType: EListingType;
  moduleType: EGRNModuleTypeForPayload;
};

export type TFabricLotListingMetadata = {
  supplierName: string;
  supplierPo: string;
  buyer: string;
  plm: string;
  po: string;
  style: string;
  lotNo: string;
  color: string;
  materialCode: string;
  inHouseDate: number;
  packed: string;
  invoice: string;
  supplierFabricInspectorReport: string;
  erp: string;
  remarks: string;
  itemCode: string;
  itemName: string;
  colorCode: string;
  stockType: string;
  lotGRNStatus: 'STARTED' | 'NOT_STARTED' | 'COMPLETED';
  itemType: EItemType.FABRIC_INSPECTION;
  fabricVariant: TFabricVariant;
  fabricType: string;
  fabricColor: string;
  fabricDescription: string;
};

type THeaderConfigDTO = {
  headerKey: string;
  displayName: string;
};

export type TFabricLotListingDTO = {
  lotId: string;
  updatedAt: number;
  metadata: TFabricLotListingMetadata;
  length: number;
  weight: number;
  width: number;
};

export type TGRNListingFilter = {
  displayKey: string;
  filterType: string;
  values: string[];
};

export type TRESPONSE_FabricGRNLotListing = {
  headerConfig: THeaderConfigDTO[];
  selectedFilters: TGRNListingFilter[];
  supportedFilters: TGRNListingFilter[];
  paginationInfo: TPaginationInfo;
  itemType: EItemType;
  items: TFabricLotListingDTO[];
};

type TTrimsLotListingMetadata = {
  color: {
    color: string;
    colorId: string;
  };
  item_name: string;
  item_id: string;
  supplier_name: string;
  supplier_po: string;
  versioned_lot_id: {
    base_lot_id: string;
    version: string;
  };
  lot_grn_status: 'STARTED' | 'NOT_STARTED' | 'COMPLETED';
};

export type TREQUEST_TrimsGRNLotListing = {
  appDetails: TMaterialRequestsAppDetails;
  filters: TGRNListingFilterRequest[];
  pageNo: number;
  pageSize: number;
  listingType: EListingType;
  moduleType: EGRNModuleTypeForPayload;
};

export type TRESPONSE_TrimsGRNLotListing = {
  headerConfig: THeaderConfigDTO[];
  selectedFilters: TGRNListingFilter[];
  supportedFilters: TGRNListingFilter[];
  paginationInfo: TPaginationInfo;
  orderedQuantities: Record<string, number>;
  receivedQuantities: Record<string, number>;
  lots: TTrimsLotListingMetadata[];
};

// ***************** Lot Details *****************

export type TREQUEST_FabricGRNLotDetails = {
  appDetails: TMaterialRequestsAppDetails;
  lotId: string;
};

export type TLotInfoDTO = {
  lotId: string;
  updatedTime: number;
  fabricVariant: TFabricVariant;
  supplierName: string;
  supplierPo: string;
  buyer: string;
  plm: string;
  po: string;
  style: string;
  fabricType: string;
  lotNo: string;
  color: string;
  fabricColor: string;
  fabricDescription: string;
  score: number;
  points: number;
  selectedRollCount: number;
  completedRollCount: number;
  lotStatus: EGRNGenerationStatus;
  inspectionStandard: TRESPONSE_LotListing['items'][number]['inspection'];
  inHouseDate: number;
  packed: string;
  invoice: string;
  supplierFabricInspectorReport: string;
  erp: string;
  remarks: string;
  fileKeyOpt: string;
  comment: string;
  itemCode: string;
  itemName: string;
  colorCode: string;
  stockType: string;
  itemType: EItemType;
  lotGrnStatus: EGRNGenerationStatus;
};

export type TRESPONSE_FabricGRNLotDetails = {
  lotInfo: TLotInfoDTO;
  rollInfoList: (Omit<TRollInfo, 'grnStatus'> & {
    grnStatus: EGRNGenerationStatus;
    grnNumber: string;
    grnDisplayTagId: number;
    grnTagId: number;
    rollSelectedForInspection: boolean;
    actualLength: number;
    actualWeight: number;
  })[];
};

export type TREQUEST_TrimsGRNLotDetails = {
  appDetails: TMaterialRequestsAppDetails;
  lotId: string;
};

type TTrimsLotDetailsRollDTO = {
  size: string;
  orderedQty: number;
  receivedQty: number;
  actualQuantity: number;
  versioned_roll_id: {
    base_roll_id: string;
    version: number;
  };
  grn_number: string;
  grn_status: EGRNGenerationStatus;
};

export type TRESPONSE_TrimsGRNLotDetails = {
  trimInspectionLotModel: {
    color: {
      color: string;
      color_id: string;
    };
    item_name: string;
    item_id: string;
    supplier_name: string;
    supplier_po: string;
    versioned_lot_id: {
      base_lot_id: string;
      version: number;
    };
    lotId: string;
  };
  rolls: TTrimsLotDetailsRollDTO[];
};

// ***************** Generate GRN Tag *****************

export type TREQUEST_GenerateGRNTag = {
  appDetails: TMaterialRequestsAppDetails;
  itemType: EItemType;
  lotId: string;
  rollIdstoActualMap: Record<string, number>;
  grnStatus: EGRNGenerationStatus;
  fabricVariant?: TFabricVariant;
};

export type TRESPONSE_GenerateGRNTag = {
  status: 'SUCCESS' | 'ERROR';
  grnNumber: number;
  errors: string[];
};
